import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { MembershipUpgrade } from './MembershipUpgrade';
import PremiumPrompt from './PremiumPrompt';

interface PremiumGateProps {
  feature: string;
  description: string;
  benefits?: { icon: string; text: string }[];
  children: React.ReactNode;
}

const PremiumGate: React.FC<PremiumGateProps> = ({
  feature,
  description,
  benefits = [],
  children,
}) => {
  const { user, loading } = useAuth();
  const { theme, currentSport } = useTheme();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);

  const ADMIN_EMAILS = ['admin@mysportscoach.online', 'sumush@gmail.com'];
  const isPremium = user && (
    user.subscription === 'premium' ||
    user.subscription === 'pro' ||
    user.role === 'admin' ||
    ADMIN_EMAILS.includes((user.email || '').toLowerCase())
  );

  if (loading) {
    return (
      <div className="screen-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Not signed in
  if (!user) {
    return (
      <>
        <div className="premium-gate">
          <div className="premium-gate-content">
            <h3>🔒 {feature}</h3>
            <p>{description}</p>
            {benefits.length > 0 && (
              <div className="premium-benefits">
                {benefits.map((b, i) => (
                  <div key={i} className="benefit-item">
                    <span className="benefit-icon">{b.icon}</span>
                    <span>{b.text}</span>
                  </div>
                ))}
              </div>
            )}
            <button
              className="upgrade-button"
              onClick={() => setShowPremiumPrompt(true)}
              style={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.surface || 'white',
              }}
            >
              Sign In to Access
            </button>
          </div>
        </div>
        <PremiumPrompt
          isOpen={showPremiumPrompt}
          onClose={() => setShowPremiumPrompt(false)}
          feature={feature}
          description={description}
        />
      </>
    );
  }

  // Signed in but free tier
  if (!isPremium) {
    return (
      <>
        <div className="premium-gate">
          <div className="premium-gate-content">
            <h3>🔒 Premium {feature}</h3>
            <p>{description}</p>
            {benefits.length > 0 && (
              <div className="premium-benefits">
                {benefits.map((b, i) => (
                  <div key={i} className="benefit-item">
                    <span className="benefit-icon">{b.icon}</span>
                    <span>{b.text}</span>
                  </div>
                ))}
              </div>
            )}
            <button
              className="upgrade-button"
              onClick={() => setShowUpgrade(true)}
              style={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.surface || 'white',
              }}
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
        {showUpgrade && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="modal-close"
                onClick={() => setShowUpgrade(false)}
              >
                ×
              </button>
              <MembershipUpgrade
                currentTier="free"
                sport={currentSport.id as 'pickleball' | 'tennis' | 'golf'}
                compact
                onSuccess={() => setShowUpgrade(false)}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
};

export default PremiumGate;
